import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Box, Collapse, Grid, Typography, Select, MenuItem, FormControl } from "@mui/material";
import PropTypes from "prop-types";

import SecondaryButton from "./SecondaryButton";
import Form from "../form/Form";

const UserFilter = ({ filterSteps, filterSchema, onFilterChange, shouldStartOpen, hideInternalButton }) => {
  const [hideFilters, setHideFilters] = useState(shouldStartOpen);
  const [currentStep, setCurrentStep] = useState(0);

  const form = useForm({
    resolver: yupResolver(filterSchema),
    mode: "onChange",
    defaultValues: {
      isAccountVerified: null,
      isEmailVerified: null,
      jobApplicationsCount: null,
    },
  });

  const flattenToFieldId = (values) => {
    const result = {};
    const flattenObj = (obj, prefix = "") => {
      Object.entries(obj).forEach(([key, val]) => {
        const fullName = prefix ? `${prefix}.${key}` : key;
        if (typeof val === "object" && val !== null && !Array.isArray(val)) {
          flattenObj(val, fullName);
        } else if (val !== null && val !== undefined && val !== "") {
          const allFields = filterSteps.flatMap((s) => s.fields);
          const matchedField = allFields.find((f) => f.name === fullName);

          result[matchedField?.fieldId || fullName] = val;
        }
      });
    };
    flattenObj(values);
    return result;
  };

  const handleSubmit = async () => {
    await form.trigger();
    if (Object.keys(form.formState.errors).length === 0) {
      const filterObject = flattenToFieldId(form.getValues());
      onFilterChange(filterObject);
    }
  };

  useEffect(() => {
    const subscription = form.watch(() => setTimeout(() => handleSubmit(), 300));
    return () => subscription.unsubscribe();
  }, [form.watch]);

  const toggleFilters = () => setHideFilters((val) => !val);

  return (
    <Box sx={{ marginBottom: "20px" }}>
      {!hideInternalButton && (
        <Box textAlign="right" marginBottom="20px">
          <SecondaryButton onClick={toggleFilters}>
            <span>{hideFilters ? "Hide Filters" : "Show Filters"}</span>
          </SecondaryButton>
        </Box>
      )}

      <Collapse orientation="vertical" in={hideFilters}>
        <Form
          steps={filterSteps}
          form={form}
          onSubmit={handleSubmit}
          startPage={currentStep}
          FormButtons={
            <Grid container sx={{ width: "40%", margin: "auto", display: "flex", justifyContent: "center" }}>
              <Grid item xs={12} sm={12} md={6} lg={6} sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "15px", fontWeight: "400", cursor: "pointer", textAlign: "center", width: "100%" }}
                  onClick={async () => {
                    await form.reset();
                    onFilterChange({});
                  }}>
                  Reset all filters
                </Typography>
              </Grid>
            </Grid>
          }
          options={{
            showTitle: true,
            sameLine: true,
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px", mt: 2, mx: { xs: 2, md: 0 } }}>
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: "10px" }}>
            {filterSteps.map((step, index) => (
              <SecondaryButton
                key={step.title}
                onClick={() => setCurrentStep(index)}
                sx={{
                  backgroundColor: currentStep === index ? "#C02026" : "transparent",
                  color: currentStep === index ? "white" : "inherit",
                }}>
                {step.title}
              </SecondaryButton>
            ))}
          </Box>

          <Box sx={{ display: { xs: "block", md: "none" }, minWidth: "200px" }}>
            <FormControl fullWidth size="small">
              <Select
                value={currentStep}
                onChange={(e) => setCurrentStep(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C02026" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C02026" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#C02026" },
                }}>
                {filterSteps.map((step, index) => (
                  <MenuItem key={step.title} value={index}>
                    {step.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

UserFilter.propTypes = {
  filterSchema: PropTypes.shape({}).isRequired,
  filterSteps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          fieldId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ).isRequired,
    }),
  ).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  shouldStartOpen: PropTypes.bool,
  hideInternalButton: PropTypes.bool,
};

UserFilter.defaultProps = {
  shouldStartOpen: false,
  hideInternalButton: false,
};

export default UserFilter;
